import { useCallback, useRef, useState } from "react";
import { useDebouncedFn } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { CheckTaxIdDocument } from "~/graphql/supergraphHooks";
import type { ScheduleCheck } from "./utils";
import type { CheckTaxIdQuery, CheckTaxIdQueryVariables } from "~/graphql/supergraphTypes";

const CHECK_DELAY_MS = 300;

export type ExistingProfileMatch = {
  taxId: string;
  taxIdType: string | null;
};

const hasExistingProfileForTaxId = async (
  taxId: string,
  arenaAccessId: number,
): Promise<boolean> => {
  try {
    const { data } = await client.query<CheckTaxIdQuery, CheckTaxIdQueryVariables>({
      query: CheckTaxIdDocument,
      variables: { taxId, arenaAccessId },
      fetchPolicy: "no-cache",
    });
    return data?.complianceRootInvestingProfileExistsByTaxId === true;
  } catch {
    return false;
  }
};

/**
 * Debounced, non-blocking tax-id lookup for the wizard. The lookup is scheduled
 * from the Yup `.test(...)` but never blocks submit; only the latest request's
 * result is applied, and unchanged values are skipped.
 */
export const useExistingProfileState = (): {
  existingProfile: ExistingProfileMatch | null;
  scheduleCheck: ScheduleCheck;
} => {
  const [existingProfile, setExistingProfile] = useState<ExistingProfileMatch | null>(null);
  const pendingRef = useRef<{
    key: string;
    value: string;
    taxIdType: string | null;
    arenaAccessId: number;
  } | null>(null);
  const checkedKeyRef = useRef<string | null>(null);
  const latestRequestIdRef = useRef(0);

  const applyMatch = useCallback((requestId: number, match: ExistingProfileMatch | null) => {
    if (requestId !== latestRequestIdRef.current) {
      return;
    }
    setExistingProfile((prev) => {
      if (!prev && !match) {
        return prev;
      }
      if (prev && match && prev.taxId === match.taxId && prev.taxIdType === match.taxIdType) {
        return prev;
      }
      return match;
    });
  }, []);

  const runCheck = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || pending.key === checkedKeyRef.current) {
      return;
    }
    checkedKeyRef.current = pending.key;
    const requestId = ++latestRequestIdRef.current;

    if (pending.value.trim().length === 0) {
      applyMatch(requestId, null);
      return;
    }
    const hasMatch = await hasExistingProfileForTaxId(pending.value, pending.arenaAccessId);
    applyMatch(
      requestId,
      hasMatch ? { taxId: pending.value, taxIdType: pending.taxIdType } : null,
    );
  }, [applyMatch]);

  const debouncedRunCheck = useDebouncedFn(runCheck, CHECK_DELAY_MS);

  const scheduleCheck = useCallback<ScheduleCheck>(
    (value, taxIdType, arenaAccessId) => {
      pendingRef.current = {
        key: `${value}|${taxIdType ?? ""}`,
        value,
        taxIdType,
        arenaAccessId,
      };
      debouncedRunCheck();
    },
    [debouncedRunCheck],
  );

  return { existingProfile, scheduleCheck };
};
